// Client-side mirror of the server's `requireTier` semantics: a paid tier
// only counts as the user's plan when its subscription is `active`.
// Celestial and above count without a live status ONLY as out-of-band grants
// (no Stripe subscription on file); a Stripe-managed subscription is always
// status-gated. A subscription in `incomplete` / `past_due` (etc.) must NOT
// be displayed as the current plan — quotas stay at Free until payment
// completes.

const PAID_ACTIVE_STATUSES = new Set(["active", "trialing", "canceling"]);
const GRANTABLE_TIERS = new Set(["celestial", "enterprise"]);

type SubscriptionUser = {
  subscriptionTier?: string | null;
  subscriptionStatus?: string | null;
  stripeSubscriptionId?: string | null;
  betaMode?: boolean;
  isAdmin?: boolean | null;
  currentPeriodEnd?: string | Date | null;
} | null | undefined;

const isOutOfBandGrant = (user: SubscriptionUser) =>
  GRANTABLE_TIERS.has((user?.subscriptionTier || "").toLowerCase()) &&
  !user?.stripeSubscriptionId;

/** The tier the user actually has access to right now. */
export function getEffectiveTier(user: SubscriptionUser): string {
  if (user?.isAdmin) return "celestial";
  // Beta mode: everyone is treated as the highest tier so no upgrade walls appear.
  if (user?.betaMode) return "celestial";
  const storedTier = (user?.subscriptionTier || "free").toLowerCase();
  const tier = storedTier === "session" ? "cosmic" : storedTier === "premium" ? "celestial" : storedTier;
  const periodExpired = !!user?.currentPeriodEnd && new Date(user.currentPeriodEnd).getTime() <= Date.now();
  if (tier === "free") return "free";
  if (periodExpired) return "free";
  if (isOutOfBandGrant(user)) return tier;
  return PAID_ACTIVE_STATUSES.has(user?.subscriptionStatus || "")
    ? tier
    : "free";
}

/** True when the user picked a paid tier but hasn't completed payment. */
export function isPaymentPending(user: SubscriptionUser): boolean {
  const storedTier = (user?.subscriptionTier || "free").toLowerCase();
  const tier = storedTier === "session" ? "cosmic" : storedTier === "premium" ? "celestial" : storedTier;
  if (tier === "free" || isOutOfBandGrant(user)) return false;
  const status = user?.subscriptionStatus || "";
  return status === "incomplete" || status === "past_due";
}

/** The paid tier awaiting payment, or null. */
export function getPendingTier(user: SubscriptionUser): string | null {
  return isPaymentPending(user)
    ? (() => {
        const tier = (user?.subscriptionTier || "").toLowerCase();
        return tier === "session" ? "cosmic" : tier === "premium" ? "celestial" : tier || null;
      })()
    : null;
}
